import React, {useEffect, useMemo, useState} from "react";
import {Category, Menu, Product} from "@/data/entities/menu";
import {formatMoney} from "@/utils/MoneyUtils";
import {Button, Icon, Table, TableData} from "peggirkit";
import {findProductById} from "@/data/entities/menu/utils";
import SoldOutFormatter from "@widgets/menuEditor/menu/categories/category/table/SoldOutFormatter";
import {FormattedMessage, useIntl} from "react-intl";
import ProductNameFormatter from "@widgets/menuEditor/menu/categories/category/table/ProductNameFormatter";
import {PencilIcon} from "@heroicons/react/16/solid";
import {EditProductModalConfig} from "@widgets/menuEditor/menu/product/EditProductModal";
import {SetStateFn} from "@/utils/SetStateUtils";

type Props = {
    menu: Menu,
    category: Category,
    isFetching: boolean,
    setEditModal: SetStateFn<EditProductModalConfig>,
};

const ProductsInCategoryTable = ({category, menu, isFetching, setEditModal}: Props) => {
    const intl = useIntl();
    const products: Product[] = useMemo(
        () => category.productIds.map(productId => findProductById(productId, menu.products)),
        [category.productIds, menu.products]);
    const [data, setData] = useState<Product[]>(products);

    // Follow the menu after a change (e.g. an edited or new product)
    useEffect(() => setData(products), [products]);

    return (
        <Table<Product>
            columns={[
                {
                    id: "name",
                    label: {displayName: intl.formatMessage({id: "product"})},
                    primary: true,
                    formatter: (_, rowData: TableData<Product>) => <ProductNameFormatter product={rowData}/>,
                },
                {
                    id: "price",
                    label: {displayName: intl.formatMessage({id: "delivering"})},
                    align: "right",
                    formatter: (price: string) => formatMoney(Number(price)),
                },
                {
                    id: "priceTakeaway",
                    label: {displayName: intl.formatMessage({id: "takingAway"})},
                    align: "right",
                    formatter: (price: string) => formatMoney(Number(price)),
                },
                {
                    id: "soldout",
                    label: {displayName: intl.formatMessage({id: "inStock"})},
                    align: "right",
                    formatter: soldout => <SoldOutFormatter soldout={soldout}/>
                },
                {
                    id: "id",
                    label: {
                        displayName: intl.formatMessage({id: "options"}),
                        hide: true,
                    },
                    align: "right",
                    formatter: (_, product) => (
                        <div className={"inline-block"}>
                            <Button
                                color={"secondary"}
                                disabled={isFetching}
                                iconBefore={<Icon icon={PencilIcon}/>}
                                buttonAction={{
                                    as: "button",
                                    actionProps: {
                                        onClick: () => setEditModal({show: true, product}),
                                    },
                                }}
                                size={"xs"}
                                breakpoints={{
                                    md: {
                                        size: "sm",
                                    },
                                }}
                            >
                                <FormattedMessage id={"edit"}/>
                            </Button>
                        </div>
                    ),
                },
            ]}
            data={data}
            setData={setData}
            size={"compact"}
            breakpoints={{
                md: {
                    size: "normal",
                },
            }}
        />
    );
};

export default ProductsInCategoryTable;